/**
 * A page menu beside a panel - Figma `6377:88365`, `3898:103320` and `6377:88386`.
 *
 * The design's legal layout: 440 for the page title over the list of sibling pages, 135
 * of gutter, 785 for what stands beside it. Expressed here as a 12-column grid - 4 / 1 /
 * 7 - which lands on the same measurements at 1360 and stays sane at every width in
 * between.
 *
 * **`submission-panel` was merged into this on 2026-09-04.** It drew the same grid with
 * the same left column, and differed only in what stood on the right: bare prose, or a
 * photo-topped card. Which one an editor gets is decided by the content, the way
 * `highlight-panel-list` decides whether a tile is a panel or a photograph - fill in an
 * image or a call to action and the column becomes a card.
 *
 * Authoring `tabs` is the third thing this does, and it changes the left column from
 * links to sibling pages into a tab list that swaps the panel beside it. `menuSide` moves
 * that list to the other side, with grid placement rather than by reordering the markup,
 * so the list still comes before its panel for a screen reader and for the keyboard.
 */
import { useId, useRef, useState } from 'react';
import { SectionShell, Blocks, CtaLink, CtaRow } from '../primitives';
import { Image, SIZES } from '../../components/ui/Image';
import LegalNav from '../shared/LegalNav';
import type { LegalListSection, Tab } from '../../types/sections';

/** The photo-topped card: the design's right-hand column, and each tab's panel. */
function Card({
  image, alt, heading, children
}: {
  image?: { url: string } | null;
  alt?: string;
  heading?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-m bg-surface-muted">
      {image ? (
        <figure className="aspect-[2/1] bg-surface-sunken">
          <Image media={image} alt={alt ?? ''} sizes={SIZES.half} className="h-full w-full object-cover" />
        </figure>
      ) : null}
      <div className="flex flex-col gap-10 bg-surface p-6 sm:p-10">
        {heading ? <h3 className="text-h4 font-normal text-ink-strong">{heading}</h3> : null}
        {children}
      </div>
    </div>
  );
}

/**
 * The tab list.
 *
 * The active tab is filled `green-400` on `green-900`, which is the treatment the design
 * already gives the current item in the legal navigation - so "selected" looks the same
 * whether it is a page or a tab.
 */
function TabList({
  tabs, active, setActive, baseId, label
}: {
  tabs: Tab[];
  active: number;
  setActive: (i: number) => void;
  baseId: string;
  label?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = tabs.length - 1;
    const next = {
      ArrowDown: active === last ? 0 : active + 1,
      ArrowRight: active === last ? 0 : active + 1,
      ArrowUp: active === 0 ? last : active - 1,
      ArrowLeft: active === 0 ? last : active - 1,
      Home: 0,
      End: last
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex w-full flex-col gap-2 lg:w-[227px]"
    >
      {tabs.map((tab, i) => {
        const selected = i === active;
        return (
          <button
            key={tab.key ?? i}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="tab"
            id={`${baseId}-tab-${i}`}
            aria-selected={selected}
            aria-controls={`${baseId}-panel-${i}`}
            // One tabstop for the list: Tab reaches it, the arrows move inside it.
            tabIndex={selected ? 0 : -1}
            onClick={() => setActive(i)}
            className={`flex items-center justify-center rounded-m p-5 text-center text-body-sm transition ${
              selected
                ? 'bg-green-400 text-green-900'
                : 'border border-grey-500 text-ink hover:border-grey-700'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default function LegalList({ data }: { data: LegalListSection }) {
  const tabs = data.tabs ?? [];
  const [active, setActive] = useState(0);
  const baseId = useId();
  const image = data.media?.image;

  const menuRight = data.menuSide === 'right';
  const menuCol = menuRight ? 'lg:col-span-4 lg:col-start-9' : 'lg:col-span-4';
  const bodyCol = menuRight ? 'lg:col-span-7 lg:col-start-1 lg:row-start-1' : 'lg:col-span-7 lg:col-start-6';

  if (tabs.length) {
    const current = tabs[Math.min(active, tabs.length - 1)];
    return (
      <SectionShell settings={data.settings} name="legal-list">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-0">
          <div className={menuCol}>
            <div className="flex flex-col gap-8 lg:gap-[72px]">
              {data.title ? (
                <h2 className="text-h3 font-normal text-ink-strong lg:text-h2">{data.title}</h2>
              ) : null}
              <TabList
                tabs={tabs}
                active={active}
                setActive={setActive}
                baseId={baseId}
                label={data.title ?? data.heading}
              />
            </div>
          </div>

          <div
            className={bodyCol}
            role="tabpanel"
            id={`${baseId}-panel-${active}`}
            aria-labelledby={`${baseId}-tab-${active}`}
            tabIndex={0}
          >
            <Card image={current.image ?? image} alt={current.label} heading={current.heading}>
              {current.body ? (
                <p className="whitespace-pre-line text-body text-ink-muted">{current.body}</p>
              ) : null}
              {current.cta ? <CtaLink value={current.cta} /> : null}
            </Card>
          </div>
        </div>
      </SectionShell>
    );
  }

  // No tabs: the sibling navigation beside the panel. A photograph or a call to action
  // makes that panel a card; with neither, it is the prose the legal pages set.
  const asCard = Boolean(image || data.ctas?.length);

  return (
    <SectionShell settings={data.settings} name="legal-list">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-0">
        <div className="lg:col-span-4">
          <LegalNav title={data.title} items={data.navItems} />
        </div>

        <div className="lg:col-span-7 lg:col-start-6">
          {asCard ? (
            <Card image={image} alt={data.media?.alt ?? ''} heading={data.heading}>
              <Blocks value={data.body} />
              {data.ctas?.length ? <CtaRow items={data.ctas} /> : null}
            </Card>
          ) : (
            <div className="flex flex-col gap-5">
              {data.heading ? (
                <h2 className="text-h4 font-normal text-ink-strong">{data.heading}</h2>
              ) : null}
              <Blocks value={data.body} className="max-w-none text-ink-strong" />
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
